import React, { useState, useEffect } from 'react';
import { Alert, Button, Card, HeadingText } from '@paypalcorp/pp-react';
import templateData from '../../utility/getTemplateData';
import { fetcher } from '../../utility/domUtils';
import OrganizerUpdatesSheet from './OrganizerUpdatesSheet';
import GenericInternalServiceError from '../../pages/error/genericInternalServiceError';
import { Message } from '@paypalcorp/worldready-react';
import { isLoggedInUser, isOneTouchUser } from '../../utility/authUtils';
import { getParameterByName } from '../../utility/queryStringHelper';
import UpdateView from './updates/UpdateView';
import { ORGANIZER_UPDATES } from '../../utility/constants';
import isEmpty from 'lodash/isEmpty';
import { handleLikeUpdate } from './organizerUpdatesUtils';
import { useHistory, useLocation } from 'react-router';
import {
  trackOrganizerUpdatesListIM,
  trackOrganizerUpdatesLoad,
  trackUpdatesListSelectionPressed,
  trackUpdatesLoadingError,
  trackUpdatesShowMorePressed,
} from '../../fpti/fpti';

const OrganizerUpdates = () => {
  const campaignId = templateData.productId;
  const [organizerUpdates, setOrganizerUpdates] = useState([]);
  const [totalUpdatesCount, setTotalUpdatesCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLandingError, setIsLandingError] = useState(false);
  const [isListError, setIsListError] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [action, setAction] = useState(''); // action that determine what content to show on sheet
  const [selectedUpdate, setSelectedUpdate] = useState({});
  const [showBackButton, setShowBackButton] = useState(false);
  const [hideSheetTitle, setHideSheetTitle] = useState(false);
  const [currentUpdate, setCurrentUpdate] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showLikeError, setShowLikeError] = useState(false);
  const [showLoading, setShowLoading] = useState(false);

  const history = useHistory();
  const location = useLocation();

  const maxUpdatesPerPage = 30;
  const closeSheet = () => {
    setIsSheetOpen(false);
    setShowLikeError(false);
  };

  const fetchOrganizerUpdates = async (page, displayError) => {
    setIsLoading(true);
    setIsLandingError(false);
    setIsListError(false);
    await fetcher(`/donate/api/campaigns/${campaignId}/updates?page=${page}`)
      .then((response) => response.json())
      .then((response) => {
        const organizerUpdatesRes = response?.data?.organizerUpdates;
        if (isEmpty(organizerUpdatesRes)) {
          trackOrganizerUpdatesLoad(false);
          if (displayError === 'landing') {
            setIsLandingError(true);
          } else {
            setIsListError(true);
          }
          return null;
        }
        trackOrganizerUpdatesLoad(true);
        trackOrganizerUpdatesListIM(organizerUpdatesRes?.total ?? 0);
        setOrganizerUpdates([
          ...organizerUpdates,
          ...(organizerUpdatesRes?.items ?? []),
        ]);
        setTotalUpdatesCount(organizerUpdatesRes?.total ?? 0);
        setIsLoading(false);
      })
      .catch(() => {
        trackUpdatesLoadingError();
        if (displayError === 'landing') {
          setIsLandingError(true);
        } else {
          setIsListError(true);
        }
        setIsLoading(false);
      });
  };

  const postUpdateReaction = (orgUpdate, isLiked) => {
    const reactions = orgUpdate.reactions;
    if (!isEmpty(reactions)) {
      const count = orgUpdate?.reactions[0]?.count ?? 0;
      return {
        ...orgUpdate,
        reactions: [
          {
            ...orgUpdate.reactions[0],
            count: isLiked ? count + 1 : count - 1,
            currentUserReacted: isLiked,
          },
        ],
      };
    }

    return {
      ...orgUpdate,
      reactions: [
        {
          reply: 'LIKE',
          count: 1,
          currentUserReacted: true,
        },
      ],
    };
  };

  const fetchSingleUpdate = async (updateId) => {
    await fetcher(`/donate/api/campaigns/${campaignId}/updates/${updateId}`)
      .then((response) => response.json())
      .then((response) => {
        const updateRes = response?.data?.singleOrgUpdate;
        setIsSheetOpen(true);
        if (isEmpty(updateRes)) {
          setShowLoading(false);
          setAction(
            ORGANIZER_UPDATES.ACTIONS.ORGANIZER_UPDATE_SINGLE_UPDATE_ERROR
          );
          return null;
        }
        setSelectedUpdate(updateRes);
        setAction(ORGANIZER_UPDATES.ACTIONS.SHOW_UPDATE_DETAIL_VIEW);

        const queryParams = new URLSearchParams(location.search);

        if (queryParams.has('update_id')) {
          queryParams.delete('update_id');
          history.replace({
            search: queryParams.toString(),
          });
        }
        return updateRes;
      })
      .then((response) => {
        if (isEmpty(response)) {
          return null;
        }
        const userReaction = response?.reactions?.[0]?.currentUserReacted;
        if (!userReaction) {
          setShowLoading(true);
          return handleLikeUpdate(userReaction, campaignId, updateId);
        }
        return null;
      })
      .then((response) => {
        setShowLoading(false);
        if (!isEmpty(response)) {
          if (response.ok) {
            setCurrentUpdate({ id: updateId, isLiked: true });
          } else {
            setShowLikeError(true);
          }
        }
      })
      .catch(() => {
        setShowLoading(false);
        setIsLoading(false);
        setAction(
          ORGANIZER_UPDATES.ACTIONS.ORGANIZER_UPDATE_SINGLE_UPDATE_ERROR
        );
      });
  };

  useEffect(() => {
    const getUpdates = async () => {
      await fetchOrganizerUpdates(currentPage, 'landing');
    };
    getUpdates();
  }, []);

  useEffect(() => {
    const getSingleUpdate = async () => {
      const updateId = getParameterByName('update_id');

      if (updateId && (isLoggedInUser() || isOneTouchUser())) {
        await fetchSingleUpdate(updateId);
      }
    };
    getSingleUpdate();
  }, []);

  const updateCurrentList = async (updateId, isLiked) => {
    const newOrgUpdate = organizerUpdates.map((orgUpdate) => {
      if (orgUpdate.fou_id !== updateId) {
        return orgUpdate;
      }

      const currentOrgUpdate = postUpdateReaction(orgUpdate, isLiked);

      if (orgUpdate.fou_id === selectedUpdate.fou_id) {
        setSelectedUpdate(currentOrgUpdate);
      }

      return currentOrgUpdate;
    });

    setOrganizerUpdates(newOrgUpdate);
  };

  useEffect(() => {
    const updateList = async () => {
      await updateCurrentList(currentUpdate.id, currentUpdate.isLiked);
    };

    updateList();
  }, [currentUpdate]);

  const handleShowMoreUpdates = () => {
    trackUpdatesShowMorePressed();
    if (isSheetOpen && currentPage * maxUpdatesPerPage < totalUpdatesCount) {
      setCurrentPage(currentPage + 1);
    }
    setShowBackButton(false);
    setHideSheetTitle(false);
    setShowLikeError(false);
    setIsSheetOpen(true);
    setAction(ORGANIZER_UPDATES.ACTIONS.SHOW_UPDATE_LIST);
  };

  const renderShowMoreUpdatesLink = () => {
    return currentPage * maxUpdatesPerPage < totalUpdatesCount;
  };

  const handleUpdatePreviewCardClick = (id) => {
    trackUpdatesListSelectionPressed(id);
    setSelectedUpdate(organizerUpdates?.find((update) => update.fou_id === id));
    setAction(ORGANIZER_UPDATES.ACTIONS.SHOW_UPDATE_DETAIL_VIEW);
    setShowBackButton(true);
    setHideSheetTitle(true);
  };

  const handleBackButtonClick = () => {
    setAction(ORGANIZER_UPDATES.ACTIONS.SHOW_UPDATE_LIST);
    setShowBackButton(false);
    setHideSheetTitle(false);
    setShowLikeError(false);
  };

  const handleFetchSingleUpdate = () => {
    const updateId = getParameterByName('update_id');
    setShowLoading(true);
    fetchSingleUpdate(updateId);
  };

  const handleFetchNextUpdates = (shouldAdd) => {
    let page = currentPage;
    if (shouldAdd) {
      page += 1;
      setCurrentPage(page);
    }
    setIsListError(false);
    fetchOrganizerUpdates(page, 'list');
  };

  return isLoading ? (
    <div style={{ height: '13rem' }}>
      <Card isLoading></Card>
    </div>
  ) : (
    <div className={`${organizerUpdates?.length > 0 && 'campaign-updates'}`}>
      <div className="update-container">
        <div className="organizer-updates">
          {isLandingError && (
            <>
              <HeadingText size="sm">
                <Message id="common.organizerUpdates.updates.errorTitle" />
              </HeadingText>
              <GenericInternalServiceError
                id="common.genericError.error.message"
                handleRetry={() =>
                  fetchOrganizerUpdates(currentPage, 'landing')
                }
              />
            </>
          )}
          {!isLandingError && organizerUpdates?.length > 0 && (
            <>
              {showLikeError && (
                <Alert type="warning" className="organizer-like-error">
                  <Message id="common.genericError.error.update.likeButton" />
                </Alert>
              )}
              <UpdateView
                update={organizerUpdates?.[0]}
                showBadge={true}
                id={`landing-${organizerUpdates?.[0].fou_id}`}
                setCurrentUpdate={setCurrentUpdate}
                setShowLikeError={setShowLikeError}
              />
            </>
          )}
        </div>
        <OrganizerUpdatesSheet
          closeSheet={closeSheet}
          isSheetOpen={isSheetOpen}
          action={action}
          totalUpdatesCount={totalUpdatesCount}
          selectedUpdate={selectedUpdate}
          organizerUpdates={organizerUpdates}
          handleUpdatePreviewCardClick={handleUpdatePreviewCardClick}
          showBackButton={showBackButton}
          handleBackButtonClick={handleBackButtonClick}
          handleShowMoreUpdates={handleShowMoreUpdates}
          renderShowMoreUpdatesLink={renderShowMoreUpdatesLink}
          hideSheetTitle={hideSheetTitle}
          isLoading={isLoading}
          setCurrentUpdate={setCurrentUpdate}
          showLikeError={showLikeError}
          setShowLikeError={setShowLikeError}
          showLoading={showLoading}
          isListError={isListError}
          fetchOrganizerUpdates={handleFetchSingleUpdate}
          fetchNextUpdates={handleFetchNextUpdates}
        />
      </div>
      {!isLandingError && totalUpdatesCount > 1 && (
        <div className="text-center">
          <Button onClick={handleShowMoreUpdates} tertiary>
            <Message id="common.organizerUpdates.updates.showMoreUpdates" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default OrganizerUpdates;
